import React, { useMemo } from "react";
import { AHWidgetLoading, WidgetOptionsProvider } from "arkh";
import Color from "color";
import { graphql, useFragment } from "react-relay";
import { BaseWidgetProps } from "./BaseProps";
import { WidgetIntegrationPushAppy } from "./WidgetIntegrationPushAppy/WidgetIntegrationPushAppy";

import { WidgetRoutineOverview } from "./WidgetRoutineOverview/WidgetRoutineOverview";
import { WidgetRoutinePositivePercentage } from "./WidgetRoutinePositivePercentage/WidgetRoutinePositivePercentage";
import { WidgetTaskCollection } from "./WidgetTaskCollection/WidgetTaskCollection";
import { WidgetTaskSingle } from "./WidgetTaskSingle/WidgetTaskSingle";
import { WidgetTeamSchedule } from "./WidgetTeamSchedule/WidgetTeamSchedule";
import { WidgetTodoOverview } from "./WidgetTodoOverview/WidgetTodoOverview";
import { WidgetSignalOverview } from "./WidgetSignalOverview/WidgetSignalOverview";
import { WidgetEmployeeRoleOverview } from "./WidgetEmployeeRoleOverview/WidgetEmployeeRoleOverview";
import { WidgetWeather } from "./WidgetWeather/WidgetWeather";
import { AllWidgetsContent_widget$key } from "./__generated__/AllWidgetsContent_widget.graphql";
import { AllWidgets_widget$key } from "./__generated__/AllWidgets_widget.graphql";
import { WidgetEmployeeSchedule } from "./WidgetEmployeeSchedule/WidgetEmployeeSchedule";

import { WidgetIntegrationTurnpike } from "./WidgetIntegrationTurnpike/WidgetIntegrationTurnpike";
import { WidgetCalendar } from "./WidgetCalendar/WidgetCalendar";
import { WidgetFile } from "./WidgetFile/WidgetFile";
import { WidgetIntegrationFlourishPraise } from "./WidgetIntegrationFlourishPraise/WidgetIntegrationFlourishPraise";
import { WidgetNote } from "./WidgetNote";
import { WidgetSpace } from "./WidgetSpace/WidgetSpace";
import { WidgetKPI } from "./WidgetKPI";
import { WidgetTodoSummary } from "./WIdgetTodoSummary/WidgetTodoSummary";
import { WidgetNewsfeed } from "./WidgetNewsfeed";
import { WidgetList } from "./WidgetList";
import { WidgetTable } from "./WidgetTable";
import { WidgetEmployeeLeave } from "./WidgetEmployeeLeave/WidgetEmployeeLeave";
import { WidgetCalendarOverview } from "./WidgetCalendarOverview/WidgetCalendarOverview";
import { WidgetHeaderOptions } from "./WidgetHeaderOptions";

function AllWidgetsContent(
  props: BaseWidgetProps & {
    spaceFocusId?: string;
    widget: AllWidgetsContent_widget$key;
    margin: number;
  }
) {
  const widget = useFragment(
    graphql`
      fragment AllWidgetsContent_widget on Widget {
        ... on WidgetSpace {
          ...WidgetSpace_widgetSpace
          __typename
        }
        ... on WidgetIntegrationFlourishPraise {
          ...WidgetIntegrationFlourishPraise_widgetIntegrationFlourishPraise
          __typename
        }
        ... on WidgetFile {
          ...WidgetFile_widgetFile
          __typename
        }
        ... on WidgetTeamSchedule {
          ...WidgetTeamSchedule_widget
          __typename
        }
        ... on WidgetEmployeeSchedule {
          ...WidgetEmployeeSchedule_widgetEmployeeSchedule
          __typename
        }
        ... on WidgetEmployeeLeave {
          ...WidgetEmployeeLeave_widgetEmployeeLeave
          __typename
        }
        ... on WidgetWeather {
          ...WidgetWeather_widget
          __typename
        }
        ... on WidgetTodoOverview {
          ...WidgetTodoOverview_widget
          __typename
        }
        ... on WidgetSignalOverview {
          ...WidgetSignalOverview_widget
          __typename
        }
        ... on WidgetEmployeeRoleOverview {
          ...WidgetEmployeeRoleOverview_widget
          __typename
        }
        ... on WidgetNote {
          ...WidgetNote_widgetNote
          __typename
        }
        ... on WidgetTaskSingle {
          ...WidgetTaskSingle_widgetTaskSingle
          __typename
        }
        ... on WidgetTaskCollection {
          ...WidgetTaskCollection_widgetTaskCollection
          __typename
        }
        ... on WidgetRoutineOverview {
          ...WidgetRoutineOverview_widgetRoutineOverview
          __typename
        }
        ... on WidgetRoutinePositivePercentage {
          ...WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage
          __typename
        }

        ... on WidgetTodoSummary {
          ...WidgetTodoSummary_widgetTodoSummary
          __typename
        }
        ... on WidgetCalendarOverview {
          ...WidgetCalendarOverview_widget
          __typename
        }
        ... on WidgetIntegrationPushAppy {
          ...WidgetIntegrationPushAppy_widgetIntegrationPushAppy
          __typename
        }
        ... on WidgetIntegrationTurnpike {
          ...WidgetIntegrationTurnpike_widgetIntegrationTurnpike
          __typename
        }
        ... on WidgetCalendar {
          ...WidgetCalendar_widgetCalendar
          __typename
        }
        ... on WidgetKPI {
          ...WidgetKPI_widgetKPI
          __typename
        }
        ... on WidgetNewsfeed {
          ...WidgetNewsfeed_widgetNewsfeed
          __typename
        }
        ... on WidgetList {
          ...WidgetList_widgetList
          __typename
        }
        ... on WidgetTable {
          ...WidgetTable_table
          __typename
        }
      }
    `,
    props.widget
  );

  switch (widget.__typename) {
    case "WidgetIntegrationFlourishPraise":
      return (
        <WidgetIntegrationFlourishPraise
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetFile":
      return (
        <WidgetFile
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetTeamSchedule":
      return (
        <WidgetTeamSchedule
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          highlightedItemId={props.highlightedItemId}
          idiom={props.idiom}
        />
      );

    case "WidgetWeather":
      return (
        <WidgetWeather
          widget={widget}
          mode={props.mode}
          storecastId={props.storecastId}
        />
      );
    case "WidgetTodoOverview":
      return (
        <WidgetTodoOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          fetchKey={props.fetchKey}
          highlightedItemId={props.highlightedItemId}
          idiom={props.idiom}
          pollFetchKey={props.pollFetchKey}
        />
      );
    case "WidgetSignalOverview":
      return (
        <WidgetSignalOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          fetchKey={props.fetchKey}
          highlightedItemId={props.highlightedItemId}
          idiom={props.idiom}
          pollFetchKey={props.pollFetchKey}
        />
      );
    case "WidgetEmployeeRoleOverview":
      return (
        <WidgetEmployeeRoleOverview
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          idiom={props.idiom}
          onEvent={props.onEvent}
          highlightedItemId={props.highlightedItemId}
        />
      );
    case "WidgetNote":
      return (
        <WidgetNote
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetTaskSingle":
      return (
        <WidgetTaskSingle
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetTaskCollection":
      return (
        <WidgetTaskCollection
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetRoutineOverview":
      return (
        <WidgetRoutineOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetRoutinePositivePercentage":
      return (
        <WidgetRoutinePositivePercentage
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetIntegrationPushAppy":
      return (
        <WidgetIntegrationPushAppy
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );

    case "WidgetIntegrationTurnpike":
      return (
        <WidgetIntegrationTurnpike
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetEmployeeLeave":
      return (
        <WidgetEmployeeLeave
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetEmployeeSchedule":
      return (
        <WidgetEmployeeSchedule
          onEvent={props.onEvent}
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetTodoSummary":
      return (
        <WidgetTodoSummary
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          onEvent={props.onEvent}
        />
      );

    case "WidgetCalendar":
      return (
        <WidgetCalendar
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetCalendarOverview":
      return (
        <WidgetCalendarOverview
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          onEvent={props.onEvent}
          fetchKey={props.fetchKey}
          idiom={props.idiom}
        />
      );
    case "WidgetSpace":
      return (
        <WidgetSpace
          {...props}
          widget={widget}
          margin={props.margin}
          spaceFocusId={props.spaceFocusId}
        />
      );
    case "WidgetKPI":
      return (
        <WidgetKPI
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetNewsfeed":
      return (
        <WidgetNewsfeed
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetList":
      return (
        <WidgetList
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          idiom={props.idiom}
          isFocused={props.isFocused}
        />
      );
    case "WidgetTable":
      return (
        <WidgetTable
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          idiom={props.idiom}
          isFocused={props.isFocused}
        />
      );
  }
  return null;
}

function AllWidgetsSuspensed(
  props: BaseWidgetProps & {
    spaceFocusId?: string;
    widget: AllWidgets_widget$key;
    margin: number;
  }
) {
  const widget = useFragment(
    graphql`
      fragment AllWidgets_widget on Widget @throwOnFieldError {
        __typename
        id
        title
        ... on WidgetWithTeams {
          teams {
            name
            icon
            color
          }
        }
        ... on WidgetWithRoles {
          roles {
            name
          }
        }
        ... on WidgetTeamSchedule {
          withoutRoleSelected
        }
        ...AllWidgetsContent_widget
      }
    `,
    props.widget
  );

  const teams = widget.teams;
  const roles = widget.roles;
  const withoutRoleSelected = widget.withoutRoleSelected;

  const headerRight = useMemo(
    () =>
      teams ? (
        <WidgetHeaderOptions
          teams={teams}
          roles={roles ?? undefined}
          withoutRoleSelected={withoutRoleSelected ?? undefined}
        />
      ) : null,
    [teams, roles, withoutRoleSelected]
  );

  const teamTintBg = useMemo(() => {
    if (teams?.length === 1 && teams[0].color) {
      return Color(teams[0].color).lightness(98).toString();
    }
    return undefined;
  }, [teams]);

  const widgetOptions = useMemo(
    () => ({ headerRight, background: teamTintBg }),
    [headerRight, teamTintBg]
  );

  return (
    <React.Suspense fallback={<AHWidgetLoading title={widget.title} />}>
      <WidgetOptionsProvider value={widgetOptions}>
        <AllWidgetsContent
          {...props}
          widget={widget}
          spaceFocusId={props.spaceFocusId}
        />
      </WidgetOptionsProvider>
    </React.Suspense>
  );
}

const Widget = React.memo(AllWidgetsSuspensed);

export {
  WidgetIntegrationPushAppy,
  WidgetNote,
  WidgetCalendarOverview,
  WidgetRoutineOverview,
  WidgetRoutinePositivePercentage,
  WidgetTaskCollection,
  WidgetTaskSingle,
  WidgetTeamSchedule,
  WidgetTodoOverview,
  WidgetSignalOverview,
  WidgetEmployeeRoleOverview,
  WidgetWeather,
  Widget as AllWidgets,
};
